import React, { useEffect, useMemo, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  IconButton,
  Typography,
  Paper,
  Modal,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import {
  AddCard as AddCardIcon,
  DeleteForever as DeleteForeverIcon,
  CleaningServices as CleaningServicesIcon,
  Add as AddIcon,
  Close as CloseIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import toast from "react-hot-toast";
import { handleError } from "../../utils/handleError";
import {
  createMembership,
  getAllMembershipsAPI,
  updateMembership,
} from "../../services/memberships/membershipService";
import { setListMemberships } from "../../redux/slice/membershipsSlice";
import { useDispatch } from "react-redux";

const FormMembershipModal = ({ membership, open, setOpen, setLoading }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const initialValues = useMemo(
    () => ({
      _id: null,
      planName: "",
      price: 0,
      description: [""],
      routeLimit: 0,
      active: false,
      currency: "MXN",
      typeUser: "user",
      billingCycle: "month",
      platform: "android",
      appleProductId: "",
      appleName: "",
      appleId:"",
      isIOSMembership: false,
    }),
    []
  );

  const [membershipData, setMembershipData] = useState(initialValues);

  useEffect(() => {
    if (membership) {
      setMembershipData((prev) => ({
        ...prev,
        ...membership,
        isIOSMembership: membership.platform[0] === "apple",
      }));
    } else {
      setMembershipData(initialValues);
    }
  }, [membership, open, initialValues]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes("description")) {
      const position = parseInt(name.split(" ")[1]);
      const newDescription = [...membershipData.description];
      newDescription[position] = value;
      setMembershipData((prevData) => ({
        ...prevData,
        description: newDescription,
      }));
    } else if (name === "price" || name === "routeLimit") {
      if (/^\d*$/.test(value)) {
        setMembershipData((prevData) => ({
          ...prevData,
          [name]: value,
        }));
      }
    } else if (name === "isIOSMembership") {
      // ✅ Cuando cambia el checkbox, actualiza automáticamente el platform
      setMembershipData((prevData) => ({
        ...prevData,
        isIOSMembership: checked,
        platform: checked ? "apple" : "android",
      }));
    } else {
      setMembershipData((prevData) => ({
        ...prevData,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const addNewInput = () => {
    setMembershipData((prevData) => ({
      ...prevData,
      description: [...prevData.description, ""],
    }));
  };

  const removeDescription = (indexToRemove) => {
    if (membershipData.description.length > 1) {
      setMembershipData((prevData) => ({
        ...prevData,
        description: prevData.description.filter(
          (_, index) => index !== indexToRemove
        ),
      }));
    } else {
      toast.error("Es necesario por lo menos un beneficio", 3500);
    }
  };

  const handleFormSubmit = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);

      const { isIOSMembership, ...dataToSend } = membershipData;
      console.log(dataToSend);

      if (membershipData?._id) {
        await updateMembership({ membershipData: dataToSend, planId: membershipData._id });
      } else {
        dataToSend.active = true;
        await createMembership({ membershipData: dataToSend });
      }

      const listMemberships = await getAllMembershipsAPI();
      dispatch(setListMemberships({ listMemberships }));
      toast.success(
        `Se ${membershipData?._id ? "actualizó" : "creó"} con éxito su membresía.`
      );
      setOpen(false);
    } catch (error) {
      const { message } = handleError(error);
      toast.error(
        `No se pudo ${membershipData?._id ? "actualizar" : "crear"
        } la membresía.\n${message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setMembershipData(initialValues);
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "80%", md: "70%", lg: "60%" },
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h5" component="h2">
              {membership ? "Editar Membresía" : "Nueva Membresía"}
            </Typography>
            <Chip
              label={membership?.active ? "Activa" : "Inactiva"}
              style={{
                backgroundColor: membership?.active
                  ? "rgb(5,193,104,20%)"
                  : "rgba(255,90,101,20%)",
                color: membership?.active ? "#14CA74" : "#FF5A65",
              }}
            />
          </Box>
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* FORM */}
        <Paper
          component="form"
          onSubmit={handleFormSubmit}
          onReset={handleReset}
          sx={{
            p: 3,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
          }}
        >
          <Grid container spacing={3}>
            {/* Nombre y tipo */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre de la Membresía"
                variant="outlined"
                name="planName"
                value={membershipData.planName}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="user-type-label">Tipo de Usuario</InputLabel>
                <Select
                  labelId="user-type-label"
                  label="Tipo de Usuario"
                  name="typeUser"
                  value={membershipData.typeUser}
                  onChange={handleChange}
                  required
                  disabled={membershipData._id !== null}
                >
                  <MenuItem value="user">Corredor</MenuItem>
                  <MenuItem value="provider">Prestador de servicio</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Período y precio */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="billingCycle-label">Período de pago</InputLabel>
                <Select
                  labelId="billingCycle-label"
                  label="Período de pago"
                  name="billingCycle"
                  value={membershipData.billingCycle}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="month">Mensualmente</MenuItem>
                  <MenuItem value="year">Anualmente</MenuItem>
                  <MenuItem value="week">Semanalmente</MenuItem>
                  <MenuItem value="day">Diariamente</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Precio de la Membresía"
                variant="outlined"
                type="number"
                name="price"
                value={membershipData.price}
                onChange={handleChange}
                InputProps={{ inputProps: { min: 0 } }}
                required
              />
            </Grid>

            {/* Límite de rutas */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Límite de Rutas"
                variant="outlined"
                type="number"
                name="routeLimit"
                value={membershipData.routeLimit}
                onChange={handleChange}
                InputProps={{ inputProps: { min: 0 } }}
                required
              />
            </Grid>

            {/* Checkbox iOS */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={membershipData.isIOSMembership}
                    onChange={handleChange}
                    name="isIOSMembership"
                    color="primary"
                  />
                }
                label="¿Es una Membresía para iOS?"
              />
            </Grid>

            {/* Advertencia iOS */}
            {membershipData.isIOSMembership && (
              <Grid item xs={12}>
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    backgroundColor: "rgba(255, 229, 100, 0.25)",
                    border: "1px solid #FFB300",
                    borderRadius: 2,
                    fontSize: "0.9rem",
                  }}
                >
                  Esta membresía está vinculada con <b>Apple Connect</b>.
                  Los cambios realizados aquí <b>no modifican</b> la configuración en Apple Connect.
                  Si realizas cambios directamente en Apple Connect, deberás actualizarlos también aquí para mantener la sincronización.
                </Alert>
              </Grid>
            )}

            {/* Inputs condicionales */}
            {membershipData.isIOSMembership && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ID del Producto en Apple Connect"
                    variant="outlined"
                    name="appleProductId"
                    value={membershipData.appleProductId}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre en Apple Connect"
                    variant="outlined"
                    name="appleName"
                    value={membershipData.appleName}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                 <Grid item xs={12} sm={6} >
                  <TextField
                    fullWidth
                    label="Apple ID"
                    variant="outlined"
                    name="appleId"
                    value={membershipData.appleId}
                    onChange={handleChange}
                    required
                  />
                </Grid>
              </>
            )}

            {/* Beneficios */}
            {membershipData.description.map((description, key) => (
              <Grid item xs={12} key={key}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    fullWidth
                    label={`Beneficio ${key + 1} de la Membresía`}
                    variant="outlined"
                    name={`description ${key}`}
                    value={description}
                    onChange={handleChange}
                    required
                  />
                  <IconButton
                    color="error"
                    onClick={() => removeDescription(key)}
                  >
                    <DeleteForeverIcon />
                  </IconButton>
                  {key === membershipData.description.length - 1 && (
                    <IconButton color="success" onClick={addNewInput}>
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}

            {/* Botones */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  style={{ backgroundColor: "#e7ef7d" }}
                  startIcon={<AddCardIcon />}
                >
                  Guardar
                </Button>
                {!membershipData._id && (
                  <Button
                    type="reset"
                    variant="contained"
                    color="error"
                    startIcon={<CleaningServicesIcon />}
                  >
                    Limpiar
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Modal>
  );
};

export default FormMembershipModal;
